import {createContext, useContext, ReactNode} from 'react';

import useMoneyCards from '~/shared/hooks/useMoneyCards';
import {ApiError} from '~/shared/services/apiErrorService';
import {api} from '~/shared/services/bffService';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import usePayments from '~/common/hooks/usePayments';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';

interface PersonalAreaContextValue {
  moneycards?: MoneyCard[];
  payments?: (UserCreditCard | UserPaypal)[] | null;
  arePaymentsLoaded: boolean;
  removePaymentMethodHandler: (args: {
    id: number;
    type: string;
  }) => Promise<boolean>;
  getUserPayments: () => Promise<void>;
  tenbisCreditBalance: number;
  isMoneyCardsLoaded: boolean;
  isMoneyCardsLoading: boolean;
  refetchMoneyCards: () => void;
  activateCardById: (id: string) => void;
  moneyCardsError?: ApiError;
}

const PersonalAreaContext = createContext<PersonalAreaContextValue | undefined>(undefined);

export const usePersonalAreaContext = () => {
  const context = useContext(PersonalAreaContext);
  if (!context) {
    throw new Error('usePersonalAreaContext must be used within PersonalAreaProvider');
  }
  return context;
};

interface PersonalAreaProviderProps {
  children: ReactNode;
}

export const PersonalAreaProvider = ({children}: PersonalAreaProviderProps) => {
  const {
    data: moneycards,
    tenbisCreditBalance,
    loaded: isMoneyCardsLoaded,
    loading: isMoneyCardsLoading,
    refetch: refetchMoneyCards,
    error: moneyCardsError,
    activateCardById,
  } = useMoneyCards({
    fetchMoneyCards: api.fetchMoneycards,
  });
  const {payments, isLoaded: arePaymentsLoaded, removePaymentMethodHandler, getUserPayments} = usePayments();

  const value: PersonalAreaContextValue = {
    moneycards,
    payments,
    arePaymentsLoaded,
    removePaymentMethodHandler,
    getUserPayments,
    tenbisCreditBalance,
    isMoneyCardsLoaded,
    isMoneyCardsLoading,
    refetchMoneyCards,
    activateCardById,
    moneyCardsError,
  };

  return (
    <PersonalAreaContext.Provider value={value}>
      {children}
    </PersonalAreaContext.Provider>
  );
};
